import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import CallProgressDashboard from '../components/CallProgressDashboard';
import TaskCard from '../components/TaskCard';
import { requirementsFromFlags } from '../components/RequirementPills';
import { useUserLocation } from '../hooks/useUserLocation';
import { routesApi, setsApi, type ScheduledSet, type RouteSummary } from '../services/api';
import { useCachedResource } from '../hooks/useCachedResource';
import { usePageRefresh } from '../context/PullToRefreshContext';

type ViewMode = 'day' | 'week' | 'month';

// Helper to get local date string (YYYY-MM-DD) without timezone shift
function toLocalDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Helper to parse YYYY-MM-DD string as local date (not UTC)
function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Get the current week's dates (Saturday to Friday - business week)
function getWeekDates(baseDate?: Date): { date: string; dayName: string; dayShort: string; isToday: boolean }[] {
  const today = new Date();
  const targetDate = baseDate || today;
  const dayOfWeek = targetDate.getDay();

  const daysSinceSaturday = (dayOfWeek + 1) % 7;
  const saturday = new Date(targetDate);
  saturday.setDate(targetDate.getDate() - daysSinceSaturday);

  const days = [];
  const dayNames = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const dayShorts = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  for (let i = 0; i < 7; i++) {
    const date = new Date(saturday);
    date.setDate(saturday.getDate() + i);
    const dateStr = toLocalDateString(date);
    days.push({
      date: dateStr,
      dayName: dayNames[i],
      dayShort: dayShorts[i],
      isToday: dateStr === toLocalDateString(today),
    });
  }

  return days;
}

// Get month dates for calendar view
function getMonthDates(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const today = new Date();
  const todayStr = toLocalDateString(today);

  // Get padding days from previous month
  const startPadding = firstDay.getDay(); // 0=Sunday
  const endPadding = 6 - lastDay.getDay();

  const days: { date: string; day: number; isCurrentMonth: boolean; isToday: boolean }[] = [];

  // Previous month padding
  for (let i = startPadding - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({
      date: toLocalDateString(d),
      day: d.getDate(),
      isCurrentMonth: false,
      isToday: false,
    });
  }

  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const d = new Date(year, month, i);
    const dateStr = toLocalDateString(d);
    days.push({
      date: dateStr,
      day: i,
      isCurrentMonth: true,
      isToday: dateStr === todayStr,
    });
  }

  // Next month padding
  for (let i = 1; i <= endPadding; i++) {
    const d = new Date(year, month + 1, i);
    days.push({
      date: toLocalDateString(d),
      day: i,
      isCurrentMonth: false,
      isToday: false,
    });
  }

  return days;
}

// View mode selector component
function ViewModeSelector({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  return (
    <div className="flex rounded-lg bg-slate-100 p-1">
      {(['day', 'week', 'month'] as ViewMode[]).map((m) => (
        <button
          key={m}
          onClick={() => onChange(m)}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all ${
            mode === m
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          {m === 'day' ? 'Day' : m === 'week' ? 'Week' : 'Month'}
        </button>
      ))}
    </div>
  );
}

export default function Schedule() {
  const navigate = useNavigate();
  const { userLocation } = useUserLocation();
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  // Cache-first routes + sets (#2057): paint last-cached instantly, revalidate
  // in the background. These fetches don't vary by the viewed date, so a single
  // cache key covers the page.
  const {
    data: scheduleData,
    isLoading,
    error,
    refetch: refetchSchedulePage,
  } = useCachedResource<{ summary: RouteSummary | null; billingMonth: string; sets: ScheduledSet[] }>(
    'schedule-page',
    async () => {
      const [routesData, setsData] = await Promise.allSettled([
        routesApi.getMyRoutes(),
        setsApi.getMySets({ status: 'all' }),
      ]);
      // Only treat it as an error (and keep any cached data) if BOTH failed.
      if (routesData.status === 'rejected' && setsData.status === 'rejected') {
        throw new Error('Failed to load schedule');
      }
      return {
        summary: routesData.status === 'fulfilled' ? routesData.value.summary : null,
        billingMonth: routesData.status === 'fulfilled' ? routesData.value.billing_month : '',
        sets: setsData.status === 'fulfilled' ? setsData.value.sets : [],
      };
    },
    []
  );
  const sets = scheduleData?.sets ?? [];
  const routeSummary = scheduleData?.summary ?? null;

  // #2089: swipe-down revalidates routes + sets.
  usePageRefresh(refetchSchedulePage);
  const billingMonth = scheduleData?.billingMonth ?? '';

  // Date navigation state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const monthDates = useMemo(() => getMonthDates(currentYear, currentMonth), [currentYear, currentMonth]);
  const today = new Date();
  const todayStr = toLocalDateString(today);

  const [selectedDay, setSelectedDay] = useState<string>(todayStr);
  const [overdueExpanded, setOverdueExpanded] = useState(false);

  // Get sets for selected day - split into scheduled and overdue
  const { scheduledSets, overdueSets } = useMemo(() => {
    const scheduled: ScheduledSet[] = [];
    const overdue: ScheduledSet[] = [];

    for (const set of sets) {
      const setDate = set.set_date.split('T')[0];
      const windowEnd = set.display_specs?.window_end;

      // Scheduled for this specific day
      if (setDate === selectedDay) {
        scheduled.push(set);
      }
      // Past-due but still within window (grace period for missed sets)
      else if (setDate < selectedDay && windowEnd && selectedDay <= windowEnd && set.status === 'pending') {
        overdue.push(set);
      }
    }

    return { scheduledSets: scheduled, overdueSets: overdue };
  }, [sets, selectedDay]);

  // Combined for backward compatibility with summary stats
  const selectedDaySets = [...scheduledSets, ...overdueSets];

  const totalSets = sets.filter(s => s.status === 'pending').length;

  // Build sets by date for calendar display: prioritize SetDate, show overdue on today
  const setsByDate = useMemo(() => {
    const map = new Map<string, ScheduledSet[]>();
    const todayStr = toLocalDateString(new Date());

    for (const set of sets) {
      const setDate = set.set_date.split('T')[0];
      const windowEnd = set.display_specs?.window_end;

      // Always add set to its assigned SetDate
      if (!map.has(setDate)) map.set(setDate, []);
      map.get(setDate)!.push(set);

      // If past-due but within window AND not already assigned to today,
      // also surface on today as a grace-period reminder. The setDate !== todayStr
      // guard prevents the same set from being counted twice in the today bucket
      // (which inflated the week-strip pending badges). (#1517)
      if (setDate < todayStr && setDate !== todayStr && windowEnd && todayStr <= windowEnd && set.status === 'pending') {
        if (!map.has(todayStr)) map.set(todayStr, []);
        if (!map.get(todayStr)!.includes(set)) {
          map.get(todayStr)!.push(set);
        }
      }
    }
    return map;
  }, [sets]);

  // Month view: count sets per day with overdue tracking
  const visitsByDate = useMemo(() => {
    const counts = new Map<string, { sets: number; completed: number; overdue: number; pending: number }>();

    for (const [date, daySets] of setsByDate) {
      const overdue = daySets.filter(s =>
        s.status === 'pending' && s.set_date.split('T')[0] < todayStr
      ).length;
      const completed = daySets.filter(s => s.status === 'completed').length;
      const pending = daySets.filter(s => s.status === 'pending').length;

      counts.set(date, {
        sets: daySets.length,
        completed,
        overdue,
        pending,
      });
    }

    return counts;
  }, [setsByDate, todayStr]);

  // Calculate light days for calls suggestion (days with fewer than 2 sets)
  const lightDays = useMemo(() => {
    const threshold = 2; // Days with fewer than 2 sets are considered "light"
    return weekDates
      .filter(day => {
        // Only consider today and future days
        if (day.date < todayStr) return false;
        const daySets = setsByDate.get(day.date) || [];
        const pendingSets = daySets.filter(s => s.status === 'pending').length;
        return pendingSets < threshold;
      })
      .map(day => day.dayShort);
  }, [weekDates, setsByDate, todayStr]);

  // Navigate weeks/months
  const navigatePrev = () => {
    if (viewMode === 'month') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else if (viewMode === 'day') {
      const prevDate = parseLocalDate(selectedDay);
      prevDate.setDate(prevDate.getDate() - 1);
      const prevStr = toLocalDateString(prevDate);
      setSelectedDay(prevStr);
      // Keep currentDate aligned so week strip + header label stay accurate
      // when day-view navigation crosses a week boundary. (#1516)
      setCurrentDate(prevDate);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    }
  };

  const navigateNext = () => {
    if (viewMode === 'month') {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    } else if (viewMode === 'day') {
      const nextDate = parseLocalDate(selectedDay);
      nextDate.setDate(nextDate.getDate() + 1);
      const nextStr = toLocalDateString(nextDate);
      setSelectedDay(nextStr);
      setCurrentDate(nextDate);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setCurrentMonth(new Date().getMonth());
    setCurrentYear(new Date().getFullYear());
    setSelectedDay(todayStr);
  };

  // Get title based on view mode
  const getTitle = () => {
    if (viewMode === 'month') {
      return new Date(currentYear, currentMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (viewMode === 'day') {
      const d = parseLocalDate(selectedDay);
      return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    }
    // Week view
    const start = weekDates[0];
    const end = weekDates[6];
    if (start && end) {
      const startDate = parseLocalDate(start.date);
      const endDate = parseLocalDate(end.date);
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
    return 'Schedule';
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <PageHeader
        title="Schedule"
        subtitle={isLoading ? 'Loading...' : `${totalSets} pending sets`}
      />

      {/* View Mode Selector */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <ViewModeSelector mode={viewMode} onChange={setViewMode} />

        {/* Date Navigation */}
        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={navigatePrev}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800">{getTitle()}</span>
            <button
              onClick={goToToday}
              className="rounded-lg bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100"
            >
              Today
            </button>
          </div>
          <button
            onClick={navigateNext}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Week View - Day Selector */}
      {viewMode === 'week' && (
        <div className="bg-white px-2 py-3 shadow-sm">
          <div className="flex justify-between">
            {weekDates.map((day) => {
              const daySets = setsByDate.get(day.date) || [];
              return (
                <button
                  key={day.date}
                  onClick={() => setSelectedDay(day.date)}
                  className={`flex flex-col items-center rounded-xl px-3 py-2 transition-all ${
                    selectedDay === day.date
                      ? 'bg-amber-100 text-amber-700'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <span className="text-xs font-medium">{day.dayShort}</span>
                  <span
                    className={`mt-1 text-lg font-bold ${
                      selectedDay === day.date ? 'text-amber-700' : 'text-slate-800'
                    }`}
                  >
                    {parseLocalDate(day.date).getDate()}
                  </span>
                  <div className="mt-1 flex justify-center gap-1">
                    {(() => {
                      const pendingCount = daySets.filter(s => s.status === 'pending').length;
                      const completedCount = daySets.filter(s => s.status === 'completed').length;
                      const overdueCount = daySets.filter(s =>
                        s.status === 'pending' && s.set_date.split('T')[0] < todayStr
                      ).length;
                      return (
                        <>
                          {pendingCount > 0 && (
                            <span className={`min-w-[16px] rounded px-1 text-[10px] font-bold text-white ${
                              overdueCount > 0 ? 'bg-red-500' : 'bg-amber-500'
                            }`}>
                              {pendingCount}
                            </span>
                          )}
                          {completedCount > 0 && (
                            <span className="min-w-[16px] rounded bg-green-500 px-1 text-[10px] font-bold text-white">
                              {completedCount}
                            </span>
                          )}
                          {daySets.length === 0 && (
                            <span className="h-4 w-4" />
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {day.isToday && (
                    <span className="mt-1 rounded-full bg-amber-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
                      Today
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Month View - Calendar Grid */}
      {viewMode === 'month' && (
        <div className="bg-white p-4 shadow-sm">
          {/* Weekday headers */}
          <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-500">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {monthDates.map((day, idx) => {
              const visits = visitsByDate.get(day.date);
              const hasVisits = visits && visits.sets > 0;
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedDay(day.date);
                    setViewMode('day');
                  }}
                  className={`aspect-square rounded-lg p-1 text-sm transition-all ${
                    day.isToday
                      ? 'bg-amber-600 font-bold text-white'
                      : day.isCurrentMonth
                      ? 'text-slate-800 hover:bg-slate-100'
                      : 'text-slate-300'
                  } ${selectedDay === day.date && !day.isToday ? 'ring-2 ring-amber-500' : ''}`}
                >
                  <div>{day.day}</div>
                  {hasVisits && (
                    <div className="mt-0.5 flex justify-center">
                      <span className={`min-w-[18px] rounded-full px-1 text-[10px] font-bold text-white ${
                        visits.overdue > 0 ? 'bg-red-500' : 'bg-amber-500'
                      }`}>
                        {visits.sets > 9 ? '9+' : visits.sets}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {/* Legend */}
          <div className="mt-3 flex justify-center gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <span className="min-w-[16px] rounded-full bg-amber-500 px-1 text-center text-[10px] font-bold text-white">2</span>
              <span>Pending</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="min-w-[16px] rounded-full bg-red-500 px-1 text-center text-[10px] font-bold text-white">1</span>
              <span>Overdue</span>
            </div>
          </div>
          {/* Note about calls */}
          <p className="mt-2 text-center text-xs text-slate-400">
            Calls are flexible and shown on the Today page
          </p>
        </div>
      )}

      {/* Main Content */}
      <main className="p-4">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="rounded-xl bg-red-50 p-4 text-center text-red-700">
            <p className="font-medium">Unable to load schedule</p>
            <p className="mt-1 text-sm">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && sets.length === 0 && (
          <div className="rounded-xl bg-slate-100 p-8 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-200">
              <svg className="h-6 w-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-700">No Sets Scheduled</h3>
            <p className="mt-1 text-sm text-slate-500">You don't have any display sets assigned.</p>
            <p className="mt-2 text-xs text-slate-400">Check the Today page for available calls.</p>
          </div>
        )}

        {/* Call Progress Dashboard (all views) */}
        {!isLoading && !error && routeSummary && (
          <div className="mb-4 space-y-3">
            <CallProgressDashboard
              totalRequired={routeSummary.total_visits_required}
              completed={routeSummary.total_visits_completed}
              remaining={routeSummary.total_visits_remaining}
              completionPercentage={routeSummary.completion_percentage}
              billingMonth={billingMonth}
            />

            {/* Light days suggestion for calls */}
            {lightDays.length > 0 && routeSummary.total_visits_remaining > 0 && (
              <div className="rounded-lg bg-cyan-50 p-3">
                <div className="flex items-center gap-2 text-sm text-cyan-800">
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span>
                    <strong>Light days for calls:</strong> {lightDays.join(', ')}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Day/Week Detail View - Shows Sets */}
        {!isLoading && !error && (viewMode === 'day' || viewMode === 'week') && (
          <>
            <h2 className="mb-3 font-heading text-lg font-semibold text-slate-800">
              {weekDates.find(d => d.date === selectedDay)?.dayName || 'Selected Day'}
            </h2>

            {/* Scheduled Sets for this day */}
            {scheduledSets.length === 0 && overdueSets.length === 0 ? (
              <div className="rounded-xl bg-slate-50 p-6 text-center">
                <p className="text-slate-500">No sets scheduled for this day</p>
                <p className="mt-1 text-xs text-slate-400">Check the Today page for available calls</p>
              </div>
            ) : (
              <>
                {scheduledSets.length > 0 && (
                  <div className="space-y-2">
                    {scheduledSets.map((set, index) => (
                      <TaskCard
                        key={set.projectset_id}
                        type="set"
                        store={{
                          store_id: set.store.store_id,
                          store_name: set.store.store_name,
                          legacy_store_id: set.store.legacy_store_id ? parseInt(set.store.legacy_store_id, 10) : undefined,
                          address: set.store.address,
                          city: set.store.city,
                          state: set.store.state,
                          lat: set.store.lat,
                          lon: set.store.lon,
                        }}
                        projectId={set.project_id}
                        projectDescription={set.project_description}
                        status={set.status === 'completed' ? 'completed' : 'pending'}
                        onClick={() => navigate(`/set/${set.projectset_id}`)}
                        setDate={set.set_date}
                        setHours={set.set_hours ?? undefined}
                        windowStart={set.display_specs?.window_start?.split('T')[0]}
                        windowEnd={set.display_specs?.window_end?.split('T')[0]}
                        setTypeName={set.set_type_name ?? undefined}
                        team={set.team}
                        requirements={requirementsFromFlags(set)}
                        showIndex={index + 1}
                        userLocation={userLocation}
                      />
                    ))}
                  </div>
                )}

                {/* Overdue Sets - Collapsible Panel */}
                {overdueSets.length > 0 && (
                  <div className="mt-4">
                    <button
                      onClick={() => setOverdueExpanded(!overdueExpanded)}
                      className="flex w-full items-center justify-between rounded-xl bg-red-50 p-4 text-left transition-all hover:bg-red-100"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium text-red-800">
                          Overdue Sets ({overdueSets.length})
                        </span>
                        <span className="text-sm text-red-600">Still within window</span>
                      </div>
                      <svg
                        className={`h-5 w-5 text-red-600 transition-transform ${overdueExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {overdueExpanded && (
                      <div className="mt-2 space-y-2">
                        {overdueSets.map((set, index) => (
                          <TaskCard
                            key={set.projectset_id}
                            type="set"
                            store={{
                              store_id: set.store.store_id,
                              store_name: set.store.store_name,
                              legacy_store_id: set.store.legacy_store_id ? parseInt(set.store.legacy_store_id, 10) : undefined,
                              address: set.store.address,
                              city: set.store.city,
                              state: set.store.state,
                              lat: set.store.lat,
                              lon: set.store.lon,
                            }}
                            projectId={set.project_id}
                            projectDescription={set.project_description}
                            status="overdue"
                            onClick={() => navigate(`/set/${set.projectset_id}`)}
                            setDate={set.set_date}
                            setHours={set.set_hours ?? undefined}
                            windowStart={set.display_specs?.window_start?.split('T')[0]}
                            windowEnd={set.display_specs?.window_end?.split('T')[0]}
                            setTypeName={set.set_type_name ?? undefined}
                            team={set.team}
                            requirements={requirementsFromFlags(set)}
                            showIndex={index + 1}
                            userLocation={userLocation}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Day Summary */}
            {selectedDaySets.length > 0 && (
              <div className="mt-4 rounded-xl bg-white p-4 shadow-card">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Sets</span>
                  <span className="font-medium text-slate-800">{selectedDaySets.length}</span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-slate-500">Completed</span>
                  <span className="font-medium text-green-600">
                    {selectedDaySets.filter((s) => s.status === 'completed').length}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-slate-500">Pending</span>
                  <span className="font-medium text-amber-600">
                    {selectedDaySets.filter((s) => s.status === 'pending').length}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

      </main>
    </div>
  );
}
